import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  Shield, 
  Lock, 
  Key, 
  AlertTriangle, 
  CheckCircle, 
  Settings,
  Users,
  FileText,
  Database,
  Smartphone
} from 'lucide-react';
import { useSecurityCompliance } from '@/hooks/useSecurityCompliance';

export const SecurityComplianceManager: React.FC = () => {
  // Mock data for demonstration - replace with actual hooks
  const securityPolicies: any[] = [];
  const complianceFrameworks: any[] = [];
  const userCompliance: any[] = [];
  const encryptionSettings: any[] = [];
  const mfaConfigurations: any[] = [];
  const isLoadingPolicies = false;

  // Mock mutation functions
  const updateSecurityPolicy = { mutate: (data: any) => console.log('Update security policy:', data) };
  const updateEncryptionSettings = { mutate: (data: any) => console.log('Update encryption:', data) };
  const updateMFAConfiguration = { mutate: (data: any) => console.log('Update MFA:', data) };

  const getComplianceColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'default';
      case 'partial': return 'secondary';
      case 'non_compliant': return 'destructive';
      default: return 'outline';
    }
  };

  const getComplianceScore = () => {
    if (!complianceFrameworks) return 0;
    const compliant = complianceFrameworks.filter(f => f.status === 'compliant').length;
    return Math.round((compliant / complianceFrameworks.length) * 100);
  };

  if (isLoadingPolicies) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-4 bg-muted rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Security Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Compliance Score</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getComplianceScore()}%</div>
            <Progress value={getComplianceScore()} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Security Policies</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{securityPolicies?.length || 0}</div>
            <p className="text-xs text-muted-foreground">
              {securityPolicies?.filter(p => p.is_active).length || 0} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">MFA Users</CardTitle>
            <Smartphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {mfaConfigurations?.filter(m => m.is_enabled).length || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              of {mfaConfigurations?.length || 0} configured
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Encryption</CardTitle>
            <Lock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {encryptionSettings?.filter(e => e.data_at_rest && e.data_in_transit).length || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Fully encrypted systems
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Security Management */}
      <Tabs defaultValue="policies" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="encryption">Encryption</TabsTrigger>
          <TabsTrigger value="mfa">MFA</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
        </TabsList>

        <TabsContent value="policies" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Security Policies</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {securityPolicies?.map((policy) => (
                <div key={policy.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{policy.name}</h4>
                      <Badge variant={policy.is_active ? 'default' : 'secondary'}>
                        {policy.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{policy.description}</p>
                    <p className="text-xs text-muted-foreground">Type: {policy.policy_type}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={policy.is_active}
                      onCheckedChange={(checked) => 
                        updateSecurityPolicy.mutate({ id: policy.id, is_active: checked })
                      }
                    />
                    <Button size="sm" variant="outline">
                      <Settings className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compliance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Compliance Frameworks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {complianceFrameworks?.map((framework) => (
                <div key={framework.id} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{framework.name}</h4>
                      <Badge variant={getComplianceColor(framework.status)}>
                        {framework.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    {framework.status === 'compliant' ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{framework.description}</p>
                  <div className="text-xs text-muted-foreground">
                    Last assessment: {new Date(framework.last_assessment).toLocaleDateString()}
                  </div>
                  <div className="mt-2">
                    <p className="text-sm font-medium mb-1">Requirements:</p>
                    <div className="grid gap-1">
                      {framework.requirements.slice(0, 3).map((req, index) => (
                        <div key={index} className="text-xs text-muted-foreground">
                          • {req}
                        </div>
                      ))}
                      {framework.requirements.length > 3 && (
                        <div className="text-xs text-muted-foreground">
                          ... and {framework.requirements.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="encryption" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Encryption Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {encryptionSettings?.map((setting) => (
                <div key={setting.id} className="p-4 border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Encryption Configuration</h4>
                    <Badge variant="outline">{setting.encryption_algorithm}</Badge>
                  </div>
                  
                  <div className="grid gap-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Data at Rest</p>
                        <p className="text-sm text-muted-foreground">Encrypt stored data</p>
                      </div>
                      <Switch
                        checked={setting.data_at_rest}
                        onCheckedChange={(checked) => 
                          updateEncryptionSettings.mutate({ id: setting.id, data_at_rest: checked })
                        }
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Data in Transit</p>
                        <p className="text-sm text-muted-foreground">Encrypt data transmission</p>
                      </div>
                      <Switch
                        checked={setting.data_in_transit}
                        onCheckedChange={(checked) => 
                          updateEncryptionSettings.mutate({ id: setting.id, data_in_transit: checked })
                        }
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Key Rotation</p>
                        <p className="text-sm text-muted-foreground">Automatic key rotation</p>
                      </div>
                      <Switch
                        checked={setting.key_rotation_enabled}
                        onCheckedChange={(checked) => 
                          updateEncryptionSettings.mutate({ id: setting.id, key_rotation_enabled: checked })
                        }
                      />
                    </div>
                  </div>
                  
                  {setting.last_key_rotation && (
                    <div className="text-xs text-muted-foreground">
                      Last key rotation: {new Date(setting.last_key_rotation).toLocaleString()}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mfa" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Multi-Factor Authentication</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {mfaConfigurations?.map((config) => (
                <div key={config.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      <span className="font-medium">User MFA Configuration</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Method: {config.method.toUpperCase()}</p>
                    <p className="text-xs text-muted-foreground">
                      Setup: {new Date(config.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={config.is_enabled ? 'default' : 'secondary'}>
                      {config.is_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <Switch
                      checked={config.is_enabled}
                      onCheckedChange={(checked) => 
                        updateMFAConfiguration.mutate({ id: config.id, is_enabled: checked })
                      }
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>User Compliance Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {userCompliance?.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span className="font-medium">User Compliance</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Status: {user.compliance_status}
                    </p>
                    {user.last_training_completed && (
                      <p className="text-xs text-muted-foreground">
                        Last training: {new Date(user.last_training_completed).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Badge variant={getComplianceColor(user.compliance_status)}>
                    {user.compliance_status}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};